import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Stadium as VenueIcon,
  Schedule as TimeIcon,
  SportsSoccer as LeagueIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { formatGameDate, formatGameTime } from '../utils/dateUtils';
import StandingComponent from '../components/standings/StandingComponent';
import PlayoffPoolStandings from '../components/standings/PlayoffPoolStandings';
import GameTypeBadge from '../components/scheduling/GameTypeBadge';
import unifiedApi from '../utils/unifiedApi';
import { useAuth } from '../contexts/AuthContext';

const League = () => {
  const { leagueSlug } = useParams();
  const { user } = useAuth();
  const _mode = user ? user.role : 'public';
  const [standings, setStandings] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [league, setLeague] = useState(null);
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [playoffStandings, setPlayoffStandings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadLeagueData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load league info, standings, schedule, upcoming games, and playoff data
        const [leagueData, standingsData, scheduleData, upcomingGamesData] = await Promise.all([
          unifiedApi.getLeagueById(leagueSlug),
          unifiedApi.getLeagueStandings(leagueSlug),
          unifiedApi.getLeagueSchedule(leagueSlug),
          unifiedApi.getUpcomingGames(leagueSlug, 4)
        ]);

        if (!standingsData && !scheduleData) {
          setError('League not found or not public');
          return;
        }

        setLeague(leagueData);
        setStandings(standingsData);
        setSchedule(scheduleData);
        setUpcomingGames(upcomingGamesData);
      } catch (err) {
        setError('Failed to load league information');
        console.error('Error loading league data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (leagueSlug) {
      loadLeagueData();
    }
  }, [leagueSlug]);

  // Load playoff data separately so it doesn't block the main page render
  useEffect(() => {
    const loadPlayoffData = async () => {
      try {
        const config = await unifiedApi.getPublicPlayoffConfig(leagueSlug);
        if (config?.isActive) {
          const playoffData = await unifiedApi.getPlayoffStandings(leagueSlug);
          if (playoffData) setPlayoffStandings(playoffData);
        }
      } catch {
        // Playoff data is optional
      }
    };

    if (leagueSlug) {
      loadPlayoffData();
    }
  }, [leagueSlug]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const leagueName = standings?.leagueName || schedule?.leagueName || 'League';

  const formatLeagueDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const seasonDates = (() => {
    const start = formatLeagueDate(league?.startDate);
    const end = formatLeagueDate(league?.endDate);
    if (start && end) return `${start} – ${end}`;
    if (start) return `Starts ${start}`;
    if (end) return `Ends ${end}`;
    return null;
  })();

  return (
    <Box>
      {/* League Header */}
      <Box className="page-header">
        <Typography variant="h4" component="h1" className="page-title">
          <LeagueIcon color="primary" />
          {leagueName}
        </Typography>
        {seasonDates ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarIcon fontSize="small" color="action" />
            <Typography variant="body1" color="text.secondary">
              {seasonDates}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary">
            League Overview
          </Typography>
        )}
      </Box>

      <Grid container spacing={4}>
        {/* Standings Section */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" component="h2" className="section-title">
              Current Standings
            </Typography>
            {standings ? (
              <StandingComponent 
                standingsData={standings}
                hideActions={true}
                showHeader={false}
                isPublic={true}
              />
            ) : (
              <Alert severity="info">No standings available</Alert>
            )}
          </Paper>

          {/* Playoff Pool Standings */}
          {playoffStandings?.pools?.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <TrophyIcon color="warning" />
                <Typography variant="h6" component="h2" className="section-title" sx={{ mb: 0 }}>
                  Playoffs
                </Typography>
              </Box>
              <Stack spacing={3}>
                {playoffStandings.pools.map((pool) => (
                  <PlayoffPoolStandings key={pool.poolId} pool={pool} scoring={playoffStandings.scoring} />
                ))}
              </Stack>
            </Paper>
          )}
        </Grid>

        {/* Upcoming Games Section */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Typography variant="h6" component="h2" className="section-title">
              Upcoming Games
            </Typography>
            {upcomingGames && upcomingGames.length > 0 ? (
              <Stack spacing={2}>
                {upcomingGames.map((game) => (
                  <Card key={game.gameId} variant="outlined" sx={{ p: 2 }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {game.homeTeamName} vs {game.awayTeamName}
                        </Typography>
                        <GameTypeBadge gameType={game.gameType ?? 0} />
                      </Box>
                      
                      <Stack spacing={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="textSecondary">
                            {formatGameDate(game.start)}
                          </Typography>
                        </Box>
                        
                        <Box display="flex" alignItems="center" gap={1}>
                          <TimeIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="textSecondary">
                            {formatGameTime(game.start)}
                          </Typography>
                        </Box>
                        
                        {game.venueName && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <VenueIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="textSecondary">
                              {game.fieldName ? `${game.fieldName}, ${game.venueName}` : game.venueName}
                            </Typography>
                          </Box>
                        )}

                        {game.description && (
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            {game.description}
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Alert severity="info">No upcoming games scheduled</Alert>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default League;